import type { Job } from '@/lib/api';

/** Dispatch outcomes that need owner follow-up while the job is still a new lead. */
export const PROBLEM_DISPATCH_STATUSES = new Set([
  'delivered_email',
  'delivered_app_only',
  'pending',
]);

export const ATTENTION_STAGE_KEY = 'new';

export function jobNeedsAttention(job: Pick<Job, 'stage_key' | 'dispatch_status'>): boolean {
  return (
    job.stage_key === ATTENTION_STAGE_KEY &&
    job.dispatch_status != null &&
    PROBLEM_DISPATCH_STATUSES.has(job.dispatch_status)
  );
}

export const CRM_ATTENTION_REFRESH_EVENT = 'crm:attention-refresh';

export function refreshCrmAttention() {
  window.dispatchEvent(new CustomEvent(CRM_ATTENTION_REFRESH_EVENT));
}

function dismissStorageKey(businessId: string) {
  return `dispafy:attention-dismiss:${businessId}`;
}

export function loadDismissedAttentionIds(businessId: string): Set<string> {
  try {
    const raw = localStorage.getItem(dismissStorageKey(businessId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

export function dismissAttentionJobs(businessId: string, jobIds: string[]) {
  const current = loadDismissedAttentionIds(businessId);
  for (const id of jobIds) current.add(id);
  localStorage.setItem(dismissStorageKey(businessId), JSON.stringify([...current]));
}

/** Drop dismissed ids for jobs that are no longer in the attention feed. */
export function pruneDismissedAttentionIds(businessId: string, activeAttentionIds: string[]) {
  const active = new Set(activeAttentionIds);
  const current = loadDismissedAttentionIds(businessId);
  const next = [...current].filter(id => active.has(id));
  if (next.length === current.size) return;
  localStorage.setItem(dismissStorageKey(businessId), JSON.stringify(next));
}

export function filterAttentionJobs(jobs: Job[], businessId: string): Job[] {
  const dismissed = loadDismissedAttentionIds(businessId);
  return jobs.filter(j => jobNeedsAttention(j) && !dismissed.has(j.id));
}
